import { WebsitePreviewOption } from '@apps/admin/common/WebsitePreview/WebsitePreview.types';
import { EventPageVisibility } from '@graphql/generated';
import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';

const category = 'website';
const pagePrefix = 'admin';
const action = 'WebsiteAdminButtonInteracted';

const actionProps = {
  action,
  category,
  pagePrefix,
};

const telemObject = createTelemetryObject({
  pages: {
    customPage: (page: string, extraInfoArgs?: unknown) => ({
      category,
      pagePrefix,
      page,
      extraPageInfo: {
        ...(extraInfoArgs || {}),
      },
    }),
    addcomponent: (page: string, pageSlug: string) => ({
      category,
      pagePrefix,
      page,
      extraPageInfo: {
        pageSlug,
      },
    }),
  },
  actions: {
    pageViewed: (page: string, extraInfoArgs?: unknown) => ({
      ...actionProps,
      action: 'PageViewed',
      page,
      extraInfo: {
        name: 'admin.website.custompage',
        ...(extraInfoArgs || {}),
      },
    }),
    updatePreviewOption: (page: string, args: { previewOption: WebsitePreviewOption }) => ({
      ...actionProps,
      page,
      label: 'Update Preview',
      extraInfo: {
        ...args,
      },
    }),
    changePageSettings: (
      page: string,
      args: {
        displayTitle: string;
        displayTitleChanged: boolean;
        pageId: string;
        url: string;
        urlChanged: boolean;
        visibilityChanged: boolean;
        visibility: EventPageVisibility;
        createdByJoy?: boolean;
      },
    ) => ({
      ...actionProps,
      action: 'PageSettingsUpdated',
      category: 'admin.pageSettingsUpdated.pageSettingsDialog',
      page,
      label: page,
      extraInfo: {
        extraInfo: {
          displayTitleTidbits: '',
          displayCustomTidbitsTitleChanged: false,
          updateLocation: 'pageSettings_Dialog',
          ...args,
        },
      },
    }),
    openWebsitePageMenu: (page: string, args: { megaNavOpened: boolean }) => ({
      ...actionProps,
      page,
      label: 'Open Website Page Menu',
      extraInfo: {
        ...args,
      },
    }),
    jumpToPage: (page: string, args: { toPageLabel: string }) => ({
      ...actionProps,
      page,
      label: 'Jump To Page',
      extraInfo: {
        ...args,
      },
    }),
    addNewPageOpen: () => ({
      action: 'WebsiteAdminButtonInteracted',
      category: 'website',
      pagePrefix: 'admin',
      label: 'Open Add New Page Dialog',
      extraInfo: {
        name: 'openAddNewPageDialog',
      },
    }),
    viewPage: (page: string) => ({
      ...actionProps,
      page,
      label: 'View Page',
    }),
    saveChanges: (page: string) => ({
      ...actionProps,
      page,
      label: 'Save Changes',
    }),
    photoEditorOpened: (page: string, label: 'opened' | 'closed') => ({
      action: 'PagePhotoEditorStatus',
      category: 'wedding',
      page,
      label,
    }),
    pagePhotoUpdated: (page: string, action: 'FocusChanged' | 'Update' | 'Delete', status: 'succeeded' | 'failed', extraInfo: object = {}) => ({
      action: 'PagePhotoUpdated',
      page,
      category,
      extraInfo: {
        name: 'pagePhotoUpdated',
        label: action,
        status,
        ...extraInfo,
      },
    }),
    homeMenu: (page: string) => ({
      ...actionProps,
      page,
      label: 'Home Menu',
    }),
    previewClickedMobile: (page: string) => ({
      ...actionProps,
      page,
      label: 'Preview Clicked Mobile',
    }),
    editClickedMobile: (page: string) => ({
      ...actionProps,
      page,
      label: 'Edit Clicked Mobile',
    }),
    addNewPage: (page: string, args: { pageSlug: string; userFeedbackOnNativePages?: string }) => ({
      category: actionProps.category,
      pagePrefix: actionProps.pagePrefix,
      action: 'WebsitePageAdded',
      page,
      label: 'Add New Page',
      extraInfo: {
        name: 'addNewPage',
        ...args,
      },
    }),
    websitePageComponentUpdated: (
      page: string,
      label: 'Add' | 'Update' | 'Delete' | 'Reorder',
      args: { action: 'added' | 'updated' | 'deleted' | 'reordered'; id?: string; componentType?: string; sortedComponentIds?: { id: string }[] },
    ) => ({
      ...actionProps,
      action: 'WebsitePageComponentUpdated',
      page,
      label: `${label} Component`,
      extraInfo: {
        name: `${label.toLocaleLowerCase()}Component`,
        ...args,
      },
    }),
    upateComponentOrder: (page: string, args: { sortedComponentIds: { id: string }[] }) => ({
      ...actionProps,
      page,
      label: 'Update Component Order',
      extraInfo: {
        ...args,
      },
    }),
    goToEditViewClicked: (page: string, componentType: string) => ({
      ...actionProps,
      page,
      action,
      label: 'Edit Custom component',
      extraInfo: {
        componentType,
      },
    }),
  },
});

const { TelemetryProvider: WebsiteEditorCustomPageTelemetryProvider, useTelemetry: useWebsiteEditorCustomPageTelemetry } = createTelemetry(telemObject);

export { WebsiteEditorCustomPageTelemetryProvider, useWebsiteEditorCustomPageTelemetry };
